import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';
import { Quiz } from '../../types';
import { QuizPlayer } from '../../components/user/QuizPlayer';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { 
  ArrowLeft, 
  Trophy, 
  Star, 
  Clock, 
  Target,
  AlertCircle,
  CheckCircle,
  Brain,
  Zap
} from 'lucide-react';

export const QuizPage: React.FC = () => {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0);

  useEffect(() => {
    if (quizId) {
      fetchQuiz();
    }
  }, [quizId]);

  const fetchQuiz = async () => {
    if (!quizId) return;
    
    try {
      const quizDoc = await getDoc(doc(db, 'quizzes', quizId));
      if (quizDoc.exists()) {
        setQuiz(quizDoc.data() as Quiz);
      } else {
        setError('Quiz not found');
      }
    } catch (error) {
      console.error('Error fetching quiz:', error);
      setError('Failed to load quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleQuizComplete = async (score: number, timeSpent: number) => {
    if (!user || !quiz) return;
    
    try {
      // Record quiz attempt
      const attemptData = {
        userId: user.uid,
        quizId: quiz.id,
        score,
        timeSpent,
        completedAt: new Date(),
        passed: score >= quiz.passScore
      };
      
      await setDoc(doc(db, 'quizAttempts', `${user.uid}_${quiz.id}_${Date.now()}`), attemptData);
      
      // Update user achievements
      await updateDoc(doc(db, 'userAchievements', user.uid), {
        totalQuizzesCompleted: increment(1),
        totalQuizScore: increment(score),
        lastQuizCompleted: new Date()
      });
      
      // Update quiz analytics
      await updateDoc(doc(db, 'quizzes', quiz.id), {
        'analytics.totalAttempts': increment(1),
        'analytics.averageScore': score, // This would need more complex calculation in real implementation
        'analytics.passRate': score >= quiz.passScore ? increment(1) : increment(0)
      });
      
      setQuizScore(score);
      setTimeSpent(timeSpent);
      setShowResults(true);
      
    } catch (error) {
      console.error('Error recording quiz completion:', error);
    }
  };

  const handleExit = () => {
    navigate(-1);
  };

  const handleRetake = () => {
    setShowResults(false);
    setQuizScore(0);
    setTimeSpent(0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !quiz) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Quiz Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The quiz you\'re looking for doesn\'t exist.'}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (showResults) {
    const passed = quizScore >= quiz.passScore;
    
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center mb-8">
          <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${
            passed ? 'bg-green-100' : 'bg-red-100'
          }`}>
            {passed ? (
              <Trophy className="w-10 h-10 text-green-600" />
            ) : (
              <AlertCircle className="w-10 h-10 text-red-600" />
            )}
          </div>
          
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {passed ? 'Congratulations!' : 'Keep Learning!'}
          </h2>
          
          <p className="text-lg text-gray-600 mb-6">
            You scored {quizScore}% {passed ? 'and passed!' : `(need ${quiz.passScore}% to pass)`}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Star className="w-5 h-5 text-yellow-500" />
                <span className="font-semibold">Score</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{quizScore}%</p>
            </div>
            
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="w-5 h-5 text-blue-500" />
                <span className="font-semibold">Time</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {Math.floor(timeSpent / 60)}:{(timeSpent % 60).toString().padStart(2, '0')}
              </p>
            </div>
            
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Target className="w-5 h-5 text-green-500" />
                <span className="font-semibold">Status</span>
              </div>
              <p className={`text-2xl font-bold ${
                passed ? 'text-green-600' : 'text-red-600'
              }`}>
                {passed ? 'Passed' : 'Failed'}
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-center space-x-4">
          <button
            onClick={handleExit}
            className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Exit Quiz
          </button>
          
          {!passed && (
            <button
              onClick={handleRetake}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retake Quiz
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleExit}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Quiz</h1>
              <p className="text-sm text-gray-600">
                {quiz.questions.length} question{quiz.questions.length !== 1 ? 's' : ''} • 
                Pass Score: {quiz.passScore}%
              </p>
            </div>
          </div>
          
          {quiz.timeLimit && (
            <div className="flex items-center space-x-2 text-red-600">
              <Clock className="w-5 h-5" />
              <span className="font-mono text-lg">
                {Math.floor(quiz.timeLimit)}:00
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Quiz Player */}
      <QuizPlayer
        quiz={quiz}
        onComplete={handleQuizComplete}
        onExit={handleExit}
      />
    </div>
  );
};
















